import { useCallback, useEffect, useState } from "react";

import { useOneTrustBannerVisible } from "./useOneTrustBannerVisible";

const isModalDismissed = (modalKey: string): boolean => {
    if (!modalKey) {
        return false;
    }

    try {
        return sessionStorage.getItem(modalKey) === "dismissed";
    } catch {
        return false;
    }
};

const markModalDismissed = (modalKey: string): void => {
    if (!modalKey) {
        return;
    }

    sessionStorage.setItem(modalKey, "dismissed");
};

export const usePopupModalIsOpen = (
    id: string,
    delaySeconds: number | null,
    hasContent: boolean,
): [boolean, (open: boolean) => void] => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const isOneTrustBannerVisible = useOneTrustBannerVisible();

    const modalKey = id || "";
    const delayMs = (delaySeconds || 6) * 1000;

    useEffect(() => {
        if (!hasContent || !modalKey || hasTriggered) {
            return;
        }

        if (isModalDismissed(modalKey)) {
            return;
        }

        if (isOneTrustBannerVisible !== false) {
            return;
        }

        const timer = setTimeout(() => {
            setIsOpen(true);
            setHasTriggered(true);
        }, delayMs);

        return () => {
            clearTimeout(timer);
        };
    }, [delayMs, hasContent, hasTriggered, isOneTrustBannerVisible, modalKey]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (!open && hasTriggered) {
                markModalDismissed(modalKey);
            }
        },
        [hasTriggered, modalKey],
    );

    return [isOpen, handleOpenChange];
};
